// Register.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { APP_CONFIG } from "../app-config";
import { apiCall } from "../utils/api";
import toast, { Toaster } from 'react-hot-toast';
import AppIcon from "../assets/image/icon.png";
import { MdLocationOn } from 'react-icons/md';
import { FaSpinner, FaExclamationCircle } from 'react-icons/fa';

const COLORS = APP_CONFIG.colors;
const URLS = APP_CONFIG.urls;

function Register() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setIsLoading] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [otpToken, setOtpToken] = useState("");
  const inputRefs = useRef([]);

  const [form, setForm] = useState({
    name: "",
    mobile: location.state?.mobile || "",
    email: "",
    referralCode: "",
  });
  const [errors, setErrors] = useState({});

  // Location state
  const [locationData, setLocationData] = useState({
    address: { city: '', state: '', country: '' },
    latitude: null,
    longitude: null,
    altitude: null,
    isLoading: true,
    error: null
  });

  const maskedMobile = form.mobile
    ? form.mobile.slice(0, 2) + "****" + form.mobile.slice(6)
    : "XXXXXXXXXX";

  // Get location on mount
  useEffect(() => {
    getLocation();
  }, []);

  const getLocation = () => {
    setLocationData(prev => ({ ...prev, isLoading: true }));

    // Check for React Native WebView
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude, altitude } = position.coords;
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
            );
            const data = await response.json();
            setLocationData({
              address: {
                city: data.address?.city || data.address?.town || data.address?.village || '',
                state: data.address?.state || '',
                country: data.address?.country || ''
              },
              latitude,
              longitude,
              altitude: altitude || null,
              isLoading: false,
              error: null
            });
          } catch (error) {
            setLocationData({
              address: { city: '', state: '', country: '' },
              latitude,
              longitude,
              altitude: altitude || null,
              isLoading: false,
              error: 'Failed to get address details'
            });
          }
        },
        (error) => {
          setLocationData({
            address: { city: '', state: '', country: '' },
            latitude: null,
            longitude: null,
            altitude: null,
            isLoading: false,
            error: error.message
          });
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    } else {
      setLocationData({
        address: { city: '', state: '', country: '' },
        latitude: null,
        longitude: null,
        altitude: null,
        isLoading: false,
        error: 'Geolocation is not supported'
      });
    }
  };

  // OTP Timer
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  // Auto-focus OTP input
  useEffect(() => {
    if (showOtp && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [showOtp]);

  // Auto-verify OTP
  useEffect(() => {
    const code = otp.join("");
    if (code.length === 6 && !verifying) {
      const timeout = setTimeout(() => {
        handleVerify();
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [otp, verifying]);

  const validate = () => {
    const errs = {};
    if (!form.name || form.name.trim().length < 2) {
      errs.name = "Name must be at least 2 characters";
    }
    if (!form.mobile || form.mobile.length !== 10 || !/^\d{10}$/.test(form.mobile)) {
      errs.mobile = "Please enter a valid 10-digit mobile number";
    }
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errs.email = "Please enter a valid email address";
    }
    if (form.referralCode && !/^[A-Za-z0-9]{6}$/.test(form.referralCode)) {
      errs.referralCode = "Referral code must be 6 alphanumeric characters";
    }
    return errs;
  };

  const handleChange = (field, value) => {
    if (field === "mobile") {
      value = value.replace(/\D/g, "").slice(0, 10);
      // Reset OTP state if user changes number
      if (showOtp) {
        setShowOtp(false);
        setOtp(["", "", "", "", "", ""]);
        setOtpToken("");
        setTimer(30);
        setCanResend(false);
      }
    }
    if (field === "referralCode") {
      value = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);
    }
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setIsLoading(true);

    try {
      const result = await apiCall(
        "register",
        {
          name: form.name,
          number: form.mobile,
          email: form.email,
          referral_code: form.referralCode,
          address: locationData.address.city || null,
          longitude: locationData.longitude || null,
          latitude: locationData.latitude || null,
          altitude: locationData.altitude || null,
        },
        false
      );

      if (result.status === true) {
        setOtpToken(result.data.otp_token);
        setShowOtp(true);
        setTimer(30);
        setCanResend(false);
        toast.success("Registration successful! Please verify your OTP.");
      } else {
        if (result.response_code === 4) {
          navigate(`/login`, { state: { mobile: form.mobile } });
        } else {
          toast.error(result.message || "Registration failed. Please try again.");
        }
      }
    } catch (error) {
      toast.error("Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = useCallback(async () => {
    const code = otp.join("");
    if (code.length !== 6) {
      toast.error("Please enter complete 6-digit OTP");
      return;
    }

    setVerifying(true);

    try {
      const result = await apiCall(
        "verify-otp",
        {
          number: form.mobile,
          otp: code,
          otp_token: otpToken,
          address: locationData.address.city || null,
          longitude: locationData.longitude || null,
          latitude: locationData.latitude || null,
          altitude: locationData.altitude || null,
          prevPage: 'register'
        },
        false
      );

      if (result.status === true) {
        toast.success("Account verified successfully!");
        localStorage.setItem("credentials", JSON.stringify(result.data));
        navigate("/home", { replace: true });
      } else {
        toast.error(result.message || "Invalid OTP. Please try again.");
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      }
    } catch (error) {
      toast.error("Network error. Please try again.");
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  }, [otp, form.mobile, otpToken, locationData, navigate]);

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    if (verifying) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    if (verifying) return;
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pastedData.length > 0) {
      const newOtp = [...otp];
      for (let i = 0; i < 6; i++) {
        newOtp[i] = pastedData[i] || "";
      }
      setOtp(newOtp);
      const focusIndex = Math.min(pastedData.length, 5);
      inputRefs.current[focusIndex]?.focus();
    }
  };

  const handleResend = async () => {
    if (!canResend || verifying || resending) return;

    setResending(true);

    try {
      const result = await apiCall(
        "register",
        {
          name: form.name,
          number: form.mobile,
          email: form.email,
          referral_code: form.referralCode,
          address: locationData.address.city || null,
          longitude: locationData.longitude || null,
          latitude: locationData.latitude || null,
          altitude: locationData.altitude || null,
        },
        false
      );

      if (result.status === true) {
        setOtpToken(result.data.otp_token);
        toast.success("OTP resent successfully!");
        setTimer(30);
        setCanResend(false);
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      } else {
        toast.error(result.message || "Failed to resend OTP. Please try again.");
      }
    } catch (error) {
      toast.error("Network error. Please try again.");
    } finally {
      setResending(false);
    }
  };

  const handleChangeNumber = () => {
    setShowOtp(false);
    setOtp(["", "", "", "", "", ""]);
    setOtpToken("");
    setTimer(30);
    setCanResend(false);
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 sm:px-6 md:px-8"
      style={{
        background: COLORS.bgColor,
      }}
    >
      <div className="w-full max-w-[480px] mx-auto">
        {/* Register Card */}
        <div
          className="flex flex-col items-center w-full"
          style={{
            padding: "32px 20px",
            borderRadius: COLORS.borderRadius,
            background: COLORS.bgColor,
            boxShadow: "0 20px 60px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.04)",
            border: `1px solid ${COLORS.borderColor}`,
          }}
        >
          {/* Logo */}
          <div
            className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center mb-3 sm:mb-4"
            style={{
              background: `linear-gradient(135deg, ${COLORS.primaryLight}, ${COLORS.bgColor})`,
              border: `1px solid ${COLORS.primaryColor}30`,
            }}
          >
            <img
              src={AppIcon}
              alt="App Icon"
              className="rounded-xl w-14 h-14 sm:w-16 sm:h-16 object-contain"
            />
          </div>

          <h1
            className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2 text-center"
            style={{
              color: COLORS.textPrimary,
            }}
          >
            {showOtp ? "Verify OTP" : "Create Account"}
          </h1>

          <p
            className="text-xs sm:text-sm mb-6 sm:mb-8 text-center"
            style={{
              color: COLORS.textSecondary,
            }}
          >
            {showOtp
              ? `OTP sent to +91 ${maskedMobile}`
              : "Join us today"
            }
          </p>

          {!showOtp ? (
            // Registration Form
            <form onSubmit={handleSubmit} className="w-full">
              {/* Full Name */}
              <div className="mb-4">
                <div
                  className="flex items-center overflow-hidden transition-colors duration-200"
                  style={{
                    border: `1px solid ${errors.name ? COLORS.errorColor : COLORS.borderColor}`,
                    borderRadius: COLORS.borderRadius,
                    background: COLORS.bgColor,
                  }}
                >
                  <div
                    className="px-3 sm:px-3.5 py-3 flex items-center justify-center border-r w-[46px] sm:w-[54px] box-border"
                    style={{
                      background: COLORS.surfaceColor,
                      borderRightColor: COLORS.borderColor,
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={COLORS.textPrimary} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={form.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    disabled={loading}
                    className="flex-1 px-3 sm:px-3.5 py-3 border-none text-sm outline-none bg-transparent disabled:opacity-60 min-w-0"
                    style={{
                      color: COLORS.textPrimary,
                    }}
                    onFocus={(e) => (e.currentTarget.parentElement.style.borderColor = COLORS.primaryColor)}
                    onBlur={(e) => (e.currentTarget.parentElement.style.borderColor = errors.name ? COLORS.errorColor : COLORS.borderColor)}
                  />
                </div>
                {errors.name && (
                  <div className="text-xs mt-1" style={{ color: COLORS.errorColor }}>
                    {errors.name}
                  </div>
                )}
              </div>

              {/* Mobile Number */}
              <div className="mb-4">
                <div
                  className="flex items-center overflow-hidden transition-colors duration-200"
                  style={{
                    border: `1px solid ${errors.mobile ? COLORS.errorColor : COLORS.borderColor}`,
                    borderRadius: COLORS.borderRadius,
                    background: COLORS.bgColor,
                  }}
                >
                  <div
                    className="px-3 sm:px-3.5 py-3 text-sm font-semibold border-r w-[46px] sm:w-[54px] box-border flex items-center justify-center"
                    style={{
                      background: COLORS.surfaceColor,
                      color: COLORS.textPrimary,
                      borderRightColor: COLORS.borderColor,
                    }}
                  >
                    +91
                  </div>
                  <input
                    type="tel"
                    maxLength={10}
                    placeholder="Mobile Number"
                    value={form.mobile}
                    onChange={(e) => handleChange("mobile", e.target.value)}
                    readOnly={!!location.state?.mobile}
                    disabled={loading}
                    className="flex-1 px-3 sm:px-3.5 py-3 border-none text-sm outline-none bg-transparent disabled:opacity-60 read-only:opacity-70 min-w-0"
                    style={{
                      color: COLORS.textPrimary,
                      fontSize: '16px',
                    }}
                  />
                </div>
                {errors.mobile && (
                  <div className="text-xs mt-1" style={{ color: COLORS.errorColor }}>
                    {errors.mobile}
                  </div>
                )}
              </div>

              {/* Email */}
              <div className="mb-4">
                <div
                  className="flex items-center overflow-hidden transition-colors duration-200"
                  style={{
                    border: `1px solid ${errors.email ? COLORS.errorColor : COLORS.borderColor}`,
                    borderRadius: COLORS.borderRadius,
                    background: COLORS.bgColor,
                  }}
                >
                  <div
                    className="px-3 sm:px-3.5 py-3 flex items-center justify-center border-r w-[46px] sm:w-[54px] box-border"
                    style={{
                      background: COLORS.surfaceColor,
                      borderRightColor: COLORS.borderColor,
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={COLORS.textPrimary} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  </div>
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={form.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    disabled={loading}
                    className="flex-1 px-3 sm:px-3.5 py-3 border-none text-sm outline-none bg-transparent disabled:opacity-60 min-w-0"
                    style={{
                      color: COLORS.textPrimary,
                    }}
                    onFocus={(e) => (e.currentTarget.parentElement.style.borderColor = COLORS.primaryColor)}
                    onBlur={(e) => (e.currentTarget.parentElement.style.borderColor = errors.email ? COLORS.errorColor : COLORS.borderColor)}
                  />
                </div>
                {errors.email && (
                  <div className="text-xs mt-1" style={{ color: COLORS.errorColor }}>
                    {errors.email}
                  </div>
                )}
              </div>

              {/* Referral Code */}
              <div className="mb-4">
                <div
                  className="flex items-center overflow-hidden transition-colors duration-200"
                  style={{
                    border: `1px solid ${errors.referralCode ? COLORS.errorColor : COLORS.borderColor}`,
                    borderRadius: COLORS.borderRadius,
                    background: COLORS.bgColor,
                  }}
                >
                  <div
                    className="px-3 sm:px-3.5 py-3 flex items-center justify-center border-r w-[46px] sm:w-[54px] box-border"
                    style={{
                      background: COLORS.surfaceColor,
                      borderRightColor: COLORS.borderColor,
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={COLORS.textPrimary} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                      <line x1="7" y1="7" x2="7.01" y2="7" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Referral Code (Optional)"
                    value={form.referralCode}
                    onChange={(e) => handleChange("referralCode", e.target.value)}
                    maxLength={6}
                    disabled={loading}
                    className="flex-1 px-3 sm:px-3.5 py-3 border-none text-sm outline-none bg-transparent disabled:opacity-60 min-w-0 uppercase"
                    style={{
                      color: COLORS.textPrimary,
                    }}
                    onFocus={(e) => (e.currentTarget.parentElement.style.borderColor = COLORS.primaryColor)}
                    onBlur={(e) => (e.currentTarget.parentElement.style.borderColor = errors.referralCode ? COLORS.errorColor : COLORS.borderColor)}
                  />
                </div>
                {errors.referralCode && (
                  <div className="text-xs mt-1" style={{ color: COLORS.errorColor }}>
                    {errors.referralCode}
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full h-11 sm:h-12 border-none rounded-lg text-sm sm:text-[15px] font-semibold transition-all duration-150 disabled:opacity-70 disabled:cursor-not-allowed active:scale-[0.98]"
                style={{
                  background: loading ? COLORS.primaryDark : COLORS.primaryColor,
                  color: COLORS.textOnPrimary,
                }}
                onMouseOver={(e) => {
                  if (!loading) e.currentTarget.style.background = COLORS.primaryDark;
                }}
                onMouseOut={(e) => {
                  if (!loading) e.currentTarget.style.background = COLORS.primaryColor;
                }}
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div
                      className="w-4 h-4 sm:w-5 sm:h-5 rounded-full animate-spin"
                      style={{
                        border: `2px solid ${COLORS.textOnPrimary}`,
                        borderTop: `2px solid transparent`,
                      }}
                    />
                    Creating Account...
                  </div>
                ) : (
                  "Create Account"
                )}
              </button>
            </form>
          ) : (
            // OTP Verification
            <div className="w-full">
              <div
                className="rounded-2xl p-4 sm:p-6 mb-4 flex flex-col items-center"
                style={{
                  background: COLORS.surfaceColor,
                  border: `1px solid ${COLORS.borderColor}`,
                }}
              >
                <div
                  className="flex gap-1.5 sm:gap-2 mb-4 justify-center w-full"
                  onPaste={handleOtpPaste}
                >
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="tel"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      disabled={verifying}
                      className="w-9 h-11 sm:w-[42px] sm:h-12 text-center text-lg sm:text-xl font-semibold rounded-xl outline-none transition-all duration-200 disabled:opacity-60"
                      style={{
                        border: `1.5px solid ${digit ? COLORS.primaryColor : COLORS.borderColor}`,
                        color: COLORS.textPrimary,
                        background: COLORS.bgColor,
                        fontSize: '18px',
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = COLORS.primaryColor;
                        e.target.style.boxShadow = `0 0 0 3px ${COLORS.primaryLight}40`;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = digit ? COLORS.primaryColor : COLORS.borderColor;
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  ))}
                </div>

                <div className="w-full flex flex-col sm:flex-row items-center justify-between gap-2 text-xs sm:text-[13px]">
                  <span style={{ color: COLORS.textSecondary }}>
                    {canResend ? (
                      <span
                        onClick={handleResend}
                        className={`font-semibold ${(verifying || resending) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        style={{ color: COLORS.primaryColor }}
                      >
                        {resending ? "Sending..." : "Resend OTP"}
                      </span>
                    ) : (
                      <span>
                        Resend in <span style={{ color: COLORS.primaryColor, fontWeight: 600 }}>{timer}s</span>
                      </span>
                    )}
                  </span>
                  <span
                    onClick={handleChangeNumber}
                    className="font-semibold cursor-pointer hover:opacity-80 transition-opacity text-xs sm:text-[13px]"
                    style={{ color: COLORS.primaryColor }}
                  >
                    Change Number
                  </span>
                </div>
              </div>

              <button
                onClick={handleVerify}
                disabled={verifying || otp.join("").length !== 6}
                className="w-full h-11 sm:h-12 border-none rounded-lg text-sm sm:text-[15px] font-semibold transition-all duration-150 disabled:opacity-70 disabled:cursor-not-allowed active:scale-[0.98]"
                style={{
                  background: verifying ? COLORS.primaryDark : COLORS.primaryColor,
                  color: COLORS.textOnPrimary,
                }}
                onMouseOver={(e) => {
                  if (!verifying) e.currentTarget.style.background = COLORS.primaryDark;
                }}
                onMouseOut={(e) => {
                  if (!verifying) e.currentTarget.style.background = COLORS.primaryColor;
                }}
              >
                {verifying ? (
                  <div className="flex items-center justify-center gap-2">
                    <div
                      className="w-4 h-4 sm:w-5 sm:h-5 rounded-full animate-spin"
                      style={{
                        border: `2px solid ${COLORS.textOnPrimary}`,
                        borderTop: `2px solid transparent`,
                      }}
                    />
                    Verifying...
                  </div>
                ) : (
                  "Verify OTP"
                )}
              </button>
            </div>
          )}

          {/* Footer section */}
          <div
            className="w-full pt-6 flex flex-col items-center border-t"
            style={{
              borderColor: COLORS.borderColor,
              marginTop: "24px",
            }}
          >
            <div
              className="text-xs sm:text-sm text-center mb-2 sm:mb-3"
              style={{
                color: COLORS.textSecondary,
              }}
            >
              Already have an account?{" "}
              <span
                onClick={() => !loading && !verifying && navigate("/login")}
                className="font-semibold"
                style={{
                  color: COLORS.primaryColor,
                  cursor: !loading && !verifying ? "pointer" : "not-allowed",
                  opacity: loading || verifying ? 0.5 : 1,
                }}
              >
                Login
              </span>
            </div>

            <div
              className="text-[10px] sm:text-xs text-center leading-relaxed px-2"
              style={{
                color: COLORS.textSecondary,
              }}
            >
              By creating, you agree to our{" "}
              <span
                onClick={() => window.open(URLS.terms)}
                className="inline-block"
                style={{
                  color: COLORS.primaryColor,
                  cursor: !loading && !verifying ? "pointer" : "not-allowed",
                  opacity: loading || verifying ? 0.5 : 1,
                }}
              >
                Terms
              </span>{" "}
              &{" "}
              <span
                onClick={() => window.open(URLS.privacy)}
                className="inline-block"
                style={{
                  color: COLORS.primaryColor,
                  cursor: !loading && !verifying ? "pointer" : "not-allowed",
                  opacity: loading || verifying ? 0.5 : 1,
                }}
              >
                Privacy
              </span>
            </div>
          </div>
        </div>

        {/* Location Status - Directly below the register card with same width */}
        <div
          className="flex items-center justify-between px-4 py-3 rounded-lg transition-all duration-200 w-full mt-3"
          style={{
            background: `linear-gradient(135deg, ${COLORS.primaryColor} 0%, ${COLORS.primaryColor}dd 100%)`,
            border: `1px solid ${COLORS.borderColor}`,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          {/* Left Section - Label with Icon */}
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 bg-white/20 rounded-full">
              <MdLocationOn className="text-white text-sm" />
            </div>
            <span className="text-white text-xs font-medium tracking-wide">
              Location
            </span>
          </div>

          {/* Right Section - Dynamic Content */}
          <div className="flex items-center gap-2">
            {locationData.address.city ? (
              <>
                <div className="text-right">
                  <div className="text-white text-xs font-medium leading-tight">
                    {locationData.address.city}
                  </div>
                  <div className="text-white/80 text-[10px] leading-tight">
                    {locationData.address.state}
                  </div>
                </div>
              </>
            ) : locationData.isLoading ? (
              <>
                <span className="text-white/90 text-xs font-medium">Fetching...</span>
                <FaSpinner className="text-white text-sm animate-spin" />
              </>
            ) : (
              <>
                <span className="text-white/80 text-xs">Unavailable</span>
                <FaExclamationCircle className="text-yellow-300 text-sm" />
              </>
            )}
          </div>
        </div>
      </div>
      <Toaster />
    </div>
  );
}

export default Register;